import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import type { OperationActionTriggerProps } from '@Constants/operations';
import { tourReportKeys } from '@Hooks/useTourReports';
import { useTourReportRunStore } from './tourReportRunStore';

// Local calendar date, not UTC — toISOString() rolls over to tomorrow once the
// local clock passes UTC midnight, which would put a future date in the picker.
const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function RunTourReportTrigger({
  soulId,
  onSuccess,
}: OperationActionTriggerProps) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const queryClient = useQueryClient();

  // Look-ahead: today through seven days out — the upcoming tours coming in soon.
  const defaultStart = useMemo(() => new Date(), []);
  const defaultEnd = useMemo(() => {
    const d = new Date(defaultStart);
    d.setDate(d.getDate() + 7);
    return d;
  }, [defaultStart]);

  const [startDate, setStartDate] = useState(toIsoDate(defaultStart));
  const [endDate, setEndDate] = useState(toIsoDate(defaultEnd));
  // Off by default — the scheduled run is what normally mails the team.
  const [sendEmail, setSendEmail] = useState(false);

  const handleRun = async () => {
    if (!startDate || !endDate) {
      toastError('Pick a start and end date.');
      return;
    }
    if (endDate < startDate) {
      toastError('End date must be on or after start date.');
      return;
    }
    setRunning(true);
    setOpen(false);
    try {
      const res = await service.runTourRollup({
        startDate,
        endDate,
        sendEmail,
      });
      if (!res?.data?.started) throw new Error(res?.message);
      // The feed shows the "thinking" thought and polls until the reports land.
      useTourReportRunStore.getState().begin(soulId);
      queryClient.invalidateQueries({
        queryKey: tourReportKeys.forSoul(soulId),
      });
      toastSuccess(
        'Tour rollup started. Reports appear as each venue finishes.',
      );
      onSuccess();
    } catch {
      toastError('Failed to start the tour rollup.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={running}>
          {running ? 'Running…' : 'Run Tour Rollup'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="flex flex-col space-y-3">
          <div>
            <Heading level="h4">Tour Rollup Range</Heading>
            <p className="mt-1 text-xs text-zinc-500">
              Upcoming wedding tours scheduled in this range. Each run adds a new
              digest to the feed.
            </p>
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="desk-tour-start">Start Date</Label>
            <Input
              id="desk-tour-start"
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="desk-tour-end">End Date</Label>
            <Input
              id="desk-tour-end"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-start gap-2 pt-1">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-zinc-600">
              Email the agent's contacts
              <span className="block text-zinc-400">
                Off by default so test runs stay quiet.
              </span>
            </span>
          </label>
          <div className="flex justify-end space-x-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={running}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleRun} loading={running}>
              {running ? 'Running…' : 'Run'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
